import { Component, OnInit, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { interval, merge, of } from 'rxjs';
import { debounceTime, throttleTime, filter, map, switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';

import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatRadioGroup, MatRadioButton} from '@angular/material/radio';
import { Jsa } from '../../../../services/jsa/jsa.interface';
import { JsaService } from '../../../../services/jsa/jsa.service';
import { JsaDraftService } from '../../../../services/jsa/jsa-draft.service';
import { JsaDraft } from '../../../../services/jsa/jsa-draft.interface';
import { Router } from '@angular/router';
import { ConnectionMonitorComponent } from "../../../x-misc/app-connection-monitor/app-connection-monitor.component";
import { CommonFunctionsService } from '../../../../services/common-functions.service';
import { SubmitDiagDirective } from '../../../../services/diagnostics/submit-diag.directive';
import { MsalService } from '@azure/msal-angular';

@Component({
    selector: 'app-jsa',
    imports: [
    SubmitDiagDirective,
    MatButtonModule,
    MatCheckboxModule,
    MatInputModule,
    MatRadioModule,
    MatFormFieldModule,
    MatCardModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckbox,
    MatSelect,
    MatOption,
    MatDatepickerModule,
    MatTimepickerModule,
    MatRadioButton,
    MatRadioGroup,
    ConnectionMonitorComponent
],
    templateUrl: './jsa.component.html',
    styleUrl: './jsa.component.css'
})
export class JsaComponent implements OnInit {
  isSubmitting = false;
  warningMessage = '';
  form: FormGroup;
  equipmentUsed = ['gpsUnit', 'totalStation', 'lineLocator', 'sx10OrSx12'];

  // Server-side draft (one per user, keyed on UPN).
  hasServerDraft = false;
  // True until the initial per-UPN draft check resolves. The form is not rendered
  // while this is true, so the gate can never pop up mid-typing after a slow GET.
  checkingDraft = true;
  // Autosave status line shown in place of the old Save Draft button.
  autosaveStatus = '';
  // Draft gate: whenever a saved draft exists, the form is blocked until the user
  // acknowledges it. On Ok the draft is loaded into the form — there is no discard
  // option, so an unsubmitted draft can only be cleared by submitting the JSA.
  draftGateOpen = false;
  private pendingDraft: JsaDraft | null = null;
  // Serialized form value of the last successful autosave — skips no-op re-saves.
  private lastSavedRaw = '';

  // sessionStorage key for the in-progress draft. The draft is persisted at submit time so
  // it survives an auth-recovery round trip (interaction-required full-page redirect, or the
  // 401/403 "refresh to sign back in" path) and is restored on the next load of this form.
  // Scoped to the tab session, so it clears naturally on tab close; explicitly cleared on a
  // successful submit.
  private readonly draftKey = 'jsa-draft';

  constructor(private fb: FormBuilder, private router: Router, private jsaService: JsaService, private jsaDraftService: JsaDraftService, private commonFuncs: CommonFunctionsService, private cdr: ChangeDetectorRef, private destroyRef: DestroyRef, private authService: MsalService) {

  }

  // Signed-in user's UPN, using the app's standard claim precedence. Used to
  // prepopulate the (still editable) email field.
  private getUpn(): string {
    const account = this.authService.instance.getActiveAccount();
    return (
      (account?.idTokenClaims?.['upn'] as string) ||
      (account?.idTokenClaims?.['preferred_username'] as string) ||
      (account?.idTokenClaims?.['email'] as string) ||
      account?.username || ''
    );
  }

  ngOnInit(): void {
    const now: Date = new Date();  // Create local date to use in form initializationf

    this.form = this.fb.group({
        email: [this.getUpn() || null, Validators.compose([Validators.required, Validators.email])],
        office: [null, Validators.required],
        clientName: [null, Validators.required],
        projectNumber: [null, Validators.required],
        projectName: [null, Validators.required],
        userDate: [now, Validators.required],
        userTime: [now, Validators.required],
        desc: [null, Validators.required],
        equipmentUsed: this.fb.array(
          [
            ...this.equipmentUsed.map(() => this.fb.control(false)),
            this.fb.control(false)
          ],
          {validators: [this.commonFuncs.atLeastOneCheckedValidator(1)]}
          ),
        hazards: this.fb.group({
          drivingTravel: [false],
          confinedSpaces: [false],
          waterHazard: [false],
          sharpEdges: [false],
          heatColdExposure: [false],
          liftingHazards: [false],
          fireExplosionHazard: [false],
          pinchCrushHazards: [false],
          electricalShock: [false],
          loneWorkers: [false],
          excavationHazard: [false],
          hazardousChemicalExposure: [false],
          roughTerrain: [false],
          potentialEnergyRelease: [false],
          highNoiseLevels: [false],
          wildlifeContact: [false],
          movingEquipment: [false],
          fallPotential: [false],
          suspendedLoads: [false],
          poisonousPlants: [false],
          other: [false]
        }),
        otherHazardText: [null],
        hospitalName: [null, Validators.required],
        hospitalAddress: [null, Validators.required],
        hospitalTele: [null, Validators.required],
        ucName: [null, Validators.required],
        ucAddress: [null, Validators.required],
        ucTele: [null, Validators.required],
        safeWork: this.fb.group({
          confinedSpacePermits: [false],
          hotWorkPermits: [false],
          lockoutTagoutPermits: [false],
          fireExtinguisherUse: [false],
          barricadeFenceUse: [false],
          specializedPPERequired: [false],
          specialPreJobSafetyTalks: [false],
          simultaneousOperations: [false],
          emergencyActionPlanReview: [false],
          trafficControlPlan: [false],
          other: [false]
        }),
        otherWorkText: [null],
        personalEquip: this.fb.group({
          hardHat: [false],
          safetyGlassesGoggles: [false],
          hiVisibilityVest: [false],
          taskSpecificGloves: [false],
          hearingProtection: [false],
          steelToeBoots: [false],
          fireRetardantClothing: [false],
          faceShields: [false],
          dustMaskRespirator: [false],
          snakeChaps: [false],
          rainGear: [false],
          chainsawChaps: [false],
          personalFlotationDevice: [false],
          dotApprovedHelmet: [false],
          other: [false]
        }),
        otherEquipText: [null],
        enviroImpact: this.fb.group({
          hazWasteDisposal: [false],
          arcSiteImpact: [false],
          wetlandContamination: [false],
          stormwaterPollution: [false],
          endangerWildlife: [false],
          releaseOfGas: [false],
          other: [false]
        }),
        otherEnvText: [null],
        stepOne: [null, Validators.required],
        hazardOne: [null, Validators.required],
        actionOne: [null, Validators.required],
        stepTwo: [null, Validators.required],
        hazardTwo: [null, Validators.required],
        actionTwo: [null, Validators.required],
        stepThree: [null, Validators.required],
        hazardThree: [null, Validators.required],
        actionThree: [null, Validators.required],

        stepFour: [null, Validators.required],
        hazardFour: [null, Validators.required],
        actionFour: [null, Validators.required],

        stepFive: [null, Validators.required],
        hazardFive: [null, Validators.required],
        actionFive: [null, Validators.required],

        ynOne: [null, Validators.required],
        ynTwo: [null, Validators.required],
        ynThree: [null, Validators.required],
        ynFour: [null, Validators.required],
        ynFive: [null, Validators.required],
        signature1: [null, Validators.required],
        signature2: [null],
        signature3: [null],
        signature4: [null],
        signature5: [null]
    });

    // Listen for changes to the equipmentUsed checkboxes to enforce "None of the Above" logic
    this.equipmentUsedArray.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(values => {
        this.enforceNoneOfTheAboveLogic(values);
      });

    // Restore a draft left behind by a prior submit that was blocked for re-auth.
    this.restoreDraft();

    // Load server-side draft state (raises the draft gate if one exists).
    this.loadServerDraftState();

    // Autosave in-progress work (idle debounce + periodic cap).
    this.setupAutosave();

    // Keep the API token warm so background autosaves don't hit an expired session.
    this.setupSilentTokenRefresh();
  }

  create(): void {
    if (this.isSubmitting) return;

    if (this.form.invalid) {
      // Surface validation state on controls that don't sit in a mat-form-field
      // (equipment checkbox group, Yes/No/N/A radio groups) — their error messages
      // are gated on `touched`, which a fresh submit wouldn't otherwise set.
      this.form.markAllAsTouched();
      this.cdr.detectChanges();
      return;
    }

    this.isSubmitting = true;
    this.warningMessage = '';

    // Persist BEFORE the request so the draft survives an auth-recovery redirect/refresh if
    // the interceptor blocks this submit for re-authentication.
    this.saveDraft();

    const newJsa: Jsa = {
      email: this.form.controls['email'].value as string,
      office: this.form.controls['office'].value as string,
      clientName: this.form.controls['clientName'].value as string,
      projectNumber: this.form.controls['projectNumber'].value as string,
      projectName: this.form.controls['projectName'].value as string,
      userDateTime: this.commonFuncs.convertToUtc(this.commonFuncs.combineDateAndTime(this.form.controls['userDate'].value, this.form.controls['userTime'].value)) as Date,
      desc: this.form.controls['desc'].value as string,
      equipmentUsed: this.selectedEquipmentUsed,
      hazards: this.extractCheckboxGroupValues(this.form.controls['hazards'] as FormGroup),
      hospitalName: this.form.controls['hospitalName'].value as string,
      hospitalAddress: this.form.controls['hospitalAddress'].value as string,
      hospitalTele: this.form.controls['hospitalTele'].value as string,
      ucName: this.form.controls['ucName'].value as string,
      ucAddress:this.form.controls['ucAddress'].value as string,
      ucTele: this.form.controls['ucTele'].value as string,
      safeWork: this.extractCheckboxGroupValues(this.form.controls['safeWork'] as FormGroup),
      personalEquip: this.extractCheckboxGroupValues(this.form.controls['personalEquip'] as FormGroup),
      enviroImpact: this.extractCheckboxGroupValues(this.form.controls['enviroImpact'] as FormGroup),
      stepOne: this.form.controls['stepOne'].value as string,
      hazardOne: this.form.controls['hazardOne'].value as string,
      actionOne: this.form.controls['actionOne'].value as string,
      stepTwo: this.form.controls['stepTwo'].value as string,
      hazardTwo: this.form.controls['hazardTwo'].value as string,
      actionTwo: this.form.controls['actionTwo'].value as string,
      stepThree: this.form.controls['stepThree'].value as string,
      hazardThree: this.form.controls['hazardThree'].value as string,
      actionThree: this.form.controls['actionThree'].value as string,

      stepFour: this.form.controls['stepFour'].value as string,
      hazardFour: this.form.controls['hazardFour'].value as string,
      actionFour: this.form.controls['actionFour'].value as string,
      stepFive: this.form.controls['stepFive'].value as string,
      hazardFive: this.form.controls['hazardFive'].value as string,
      actionFive: this.form.controls['actionFive'].value as string,


      ynOne: this.form.controls['ynOne'].value as string,
      ynTwo: this.form.controls['ynTwo'].value as string,
      ynThree: this.form.controls['ynThree'].value as string,
      ynFour: this.form.controls['ynFour'].value as string,
      ynFive: this.form.controls['ynFive'].value as string,
      signature1: this.form.controls['signature1'].value as string,
      signature2: this.form.controls['signature2'].value as string,
      signature3: this.form.controls['signature3'].value as string, 
      signature4: this.form.controls['signature4'].value as string,
      signature5: this.form.controls['signature5'].value as string,
    };
    this.jsaService.createJsa(newJsa)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.clearDraft();
          this.router.navigate(['success']);
          this.isSubmitting = false;
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.isSubmitting = false;
          this.warningMessage = this.submissionErrorMessage(err);
          this.cdr.detectChanges();
        },
      });
}

  // --- Draft persistence (sessionStorage) ---------------------------------------------
  // Best-effort: storage can be unavailable (private mode / quota) — never let it break a
  // submit or a page load.

  private saveDraft(): void {
    try {
      sessionStorage.setItem(this.draftKey, JSON.stringify(this.form.getRawValue()));
    } catch {
      // storage unavailable — draft simply won't survive; submit proceeds normally.
    }
  }

  private clearDraft(): void {
    try {
      sessionStorage.removeItem(this.draftKey);
    } catch {
      // ignore
    }
  }

  private restoreDraft(): void {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(this.draftKey);
    } catch {
      return;
    }
    if (!raw) return;

    try {
      const draft = JSON.parse(raw);
      // Date/time controls serialize to ISO strings; revive them so the Material
      // datepicker/timepicker receive Date objects, not strings.
      if (draft.userDate) draft.userDate = new Date(draft.userDate);
      if (draft.userTime) draft.userTime = new Date(draft.userTime);
      this.form.patchValue(draft);
      this.warningMessage =
        'We restored your unsaved JSA from a previous attempt. Please review the entries, then resubmit.';
      this.cdr.detectChanges();
    } catch {
      // Corrupt/incompatible draft — drop it rather than surfacing an error.
      this.clearDraft();
    }
  }

  // --- Server-side draft (per-UPN) ----------------------------------------------------

  private loadServerDraftState(): void {
    this.jsaDraftService.getDraft()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ data }) => {
          if (data) {
            // Any saved draft blocks the form until the user loads it.
            this.hasServerDraft = true;
            this.pendingDraft = data;
            this.draftGateOpen = true;
          }
          this.checkingDraft = false;
          this.cdr.detectChanges();
        },
        // Best-effort: a draft-load failure must not block the blank form.
        error: () => {
          this.checkingDraft = false;
          this.cdr.detectChanges();
        },
      });
  }

  // Autosave the in-progress form to the server draft. Two triggers, merged:
  //   • idle:   4s after the last edit (debounce)
  //   • capped: at most once every 30s of continuous editing (throttle, trailing)
  // Guards: never save while the draft gate is open, never save a pristine form, and
  // skip no-op saves (identical serialized value). switchMap means the latest edit
  // wins — an in-flight save is superseded by a newer one. Errors are swallowed and
  // retried on the next trigger, so a transient failure never interrupts the user.
  private setupAutosave(): void {
    const changes$ = this.form.valueChanges.pipe(
      filter(() => !this.draftGateOpen && this.form.dirty),
    );
    const idle$ = changes$.pipe(debounceTime(4000));
    const capped$ = changes$.pipe(throttleTime(30000, undefined, { leading: false, trailing: true }));

    merge(idle$, capped$)
      .pipe(
        map(() => JSON.stringify(this.form.getRawValue())),
        filter(raw => raw !== this.lastSavedRaw),
        switchMap(raw => {
          this.autosaveStatus = 'Saving…';
          this.cdr.detectChanges();
          // Persist to sessionStorage first so work survives an auth-recovery redirect
          // triggered by this very request.
          this.saveDraft();
          return this.jsaDraftService.upsertDraft(this.form.getRawValue()).pipe(
            map(() => raw),
            catchError(() => {
              this.autosaveStatus = 'Could not save — will retry.';
              this.cdr.detectChanges();
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(raw => {
        if (raw === null) return;
        this.lastSavedRaw = raw;
        this.hasServerDraft = true;
        // Persistent confirmation with the local save time, e.g. "Saved draft at 3:05 pm CDT."
        this.autosaveStatus = `Saved draft at ${this.formatSavedTimestamp(new Date())}.`;
        this.cdr.detectChanges();
      });
  }

  // Local-time stamp for the autosave confirmation: "h:mm am/pm <tz>" (e.g. "3:05 pm CDT").
  private formatSavedTimestamp(date: Date): string {
    const time = date
      .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      .replace('AM', 'am')
      .replace('PM', 'pm');
    const tz = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(date)
      .find(part => part.type === 'timeZoneName')?.value ?? '';
    return tz ? `${time} ${tz}` : time;
  }

  // Proactively refresh the API token every 15 minutes so a background autosave never
  // hits an expired session (which would trigger a jarring mid-form redirect). Failures
  // are swallowed — this is a best-effort warm-up, not an interactive re-auth.
  private setupSilentTokenRefresh(): void {
    interval(15 * 60 * 1000)
      .pipe(
        switchMap(() => {
          const account = this.authService.instance.getActiveAccount();
          if (!account) return of(null);
          return this.authService
            .acquireTokenSilent({ scopes: environment.apiConfig.scopes, account })
            .pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  // Draft gate — acknowledge: apply the draft into a usable form so the user can finish
  // and submit normally (a successful submit deletes the draft backend-side).
  loadSavedDraft(): void {
    if (this.pendingDraft) this.applyDraftToForm(this.pendingDraft.formData);
    this.draftGateOpen = false;
    // Treat the just-loaded draft as the baseline so autosave doesn't immediately
    // re-save identical data; a subsequent edit will trigger a normal save.
    this.lastSavedRaw = JSON.stringify(this.form.getRawValue());
    this.cdr.detectChanges();
  }

  // Shared applier — mirrors restoreDraft()'s date revival, then patches wholesale.
  // patchValue restores the equipmentUsed FormArray (boolean[]), the nested checkbox
  // groups, and the ynOne..ynFive radio strings.
  private applyDraftToForm(formData: any): void {
    if (!formData) return;
    const draft = { ...formData };
    if (draft.userDate) draft.userDate = new Date(draft.userDate);
    if (draft.userTime) draft.userTime = new Date(draft.userTime);
    this.form.patchValue(draft);
    this.cdr.detectChanges();
  }

  private submissionErrorMessage(err: HttpErrorResponse): string {
    const preserved = 'Your form data has been preserved.';
    if (err?.status === 401 || err?.status === 403) {
      return `Your session has expired or you've been signed out. Please refresh the page to sign back in, then resubmit. ${preserved}`;
    }
    if (err?.status === 0) {
      return `Submission failed — we couldn't reach the server. Please check your connection and try again. ${preserved}`;
    }
    return `Submission failed. Please try again in a moment. ${preserved}`;
  }

  private enforceNoneOfTheAboveLogic(values: boolean[]) {
    const noneIndex = this.equipmentUsed.length; // Assuming noneOfTheAbove is the last control in the array
    const isNoneChecked = values[noneIndex];

    // Case 1: If "None of the Above" is checked, uncheck all other options
    if (isNoneChecked) {
      this.equipmentUsed.forEach((_, i) => {
        if (values[i] === true) {
          this.equipmentUsedArray.at(i).setValue(false, { emitEvent: false });
        }
      });

    // Case 2: If any other option is checked, uncheck "None of the Above"
    } else {
      const anyRealChecked = values.slice(0, noneIndex).some(v => v);
      if (anyRealChecked && values[noneIndex] === true) {
        this.equipmentUsedArray.at(noneIndex).setValue(false, { emitEvent: false });
      }
    }
  }

  formatEquipmentLabel(equipment: string): string {
    switch (equipment) {
      case 'gpsUnit': return 'GPS Unit';
      case 'totalStation': return 'Total Station';
      case 'lineLocator': return 'Line Locator';
      case 'sx10OrSx12': return 'SX10 or SX12';
      default: return equipment;
    }
  }

  get equipmentUsedArray(): FormArray {
    return this.form.get('equipmentUsed') as FormArray;
  }

  get selectedEquipmentUsed(): string[] {
    const values = this.equipmentUsedArray.value as boolean[];
    const noneIndex = this.equipmentUsed.length; // Assuming noneOfTheAbove is the last control in the array
    
    if (values[noneIndex] === true) {
      return ['noneOfTheAbove'];
    }

    // Otherwise return the list of selected equipment based on the boolean values in the array
    return this.equipmentUsed
      .map((label, i) => values[i] ? label : null)
      .filter((v): v is string => v != null) as string[];
  }

  private extractCheckboxGroupValues(group: FormGroup): string[] {
    return Object.keys(group.controls).filter(key => group.controls[key].value);
  }

  otherWork = false;
  otherWorkText = '';

  otherHazard = false;
  otherHazardText = '';

  otherEquip = false;
  otherEquipText = '';

  otherEnv = false;
  otherEnvText = '';

  jobStep1: string = '';
  hazard1: string = '';
  action1: string = '';

  jobStep2: string = '';
  hazard2: string = '';
  action2: string = '';

  jobStep3: string = '';
  hazard3: string = '';
  action3: string = '';

  jobStep4: string = '';
  hazard4: string = '';
  action4: string = '';

  jobStep5: string = '';
  hazard5: string = '';
  action5: string = '';

  rAnswer1: string = '';
  rAnswer2: string = '';
  rAnswer3: string = '';
  rAnswer4: string = '';
  rAnswer5: string = '';

  showAdditionalSignatures: boolean = false;

  onJobStepChange(step: number) {
    if (step === 1 && this.jobStep1) {
      this.hazard1 = '';
    } else if (step === 2 && this.jobStep2) {
      this.hazard2 = '';
    } else if (step === 3 && this.jobStep3) {
      this.hazard3 = '';
    } else if (step === 4 && this.jobStep4) {
      this.hazard4 = '';
    } else if (step === 5 && this.jobStep5) {
      this.hazard5 = '';
    }
  }

  onHazardChange(step: number) {
    if (step === 1 && this.hazard1) {
      this.action1 = '';
    } else if (step === 2 && this.hazard2) {
      this.action2 = '';
    } else if (step === 3 && this.hazard3) {
      this.action3 = '';
    } else if (step === 4 && this.hazard4) {
      this.action4 = '';
    } else if (step === 5 && this.hazard5) {
      this.action5 = '';
    } 
  }

}
